import { useMutation } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowUpRight,
  Mail,
  Phone,
  RefreshCw,
  Send,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { useAuth } from '../auth/AuthProvider'
import type { OnboardingApiClient } from '../onboarding/onboarding-api'
import type {
  ConfirmedOrganizationClinic,
  ManagerAccess,
  ManagerInvitation,
} from '../onboarding/organization-onboarding-contracts'
import { usePatientDialog } from '../patients/use-patient-dialog'
import type { ReadSideRepository } from '../read-side/read-side-status'
import { useTenant } from '../tenant/TenantProvider'
import {
  emailProblem,
  initials,
  invitationError,
  invitationStatus,
  managerAccessError,
  managerLabel,
  onboardingStep,
  phoneNumberLabel,
  plural,
  cancelInvitationError,
} from './organization-model'
import {
  Status,
  useClinicStatus,
  type ManagerAccessView,
} from './organization-clinic-status'
import panel from './SettingsSection.module.css'
import styles from './OrganizationSettings.module.css'

function operationId(action: 'revoke' | 'grant') {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
  return `dashboard-manager-${action}-${random}`.slice(0, 128)
}

type Confirmation =
  | { kind: 'remove'; manager: ManagerAccess }
  | { kind: 'cancel'; invitation: ManagerInvitation }

/**
 * One clinic: its phone & texting, managers and invitations. Removing a
 * manager or cancelling an invitation asks first, inside this dialog; every
 * change is authorized again by the server with the exact revision shown.
 */
export function OrganizationClinicDialog({
  clinic,
  api,
  readSide,
  managerAccess,
  notify,
  onOpenClinicSettings,
  onClose,
}: {
  clinic: ConfirmedOrganizationClinic
  api: OnboardingApiClient
  readSide: ReadSideRepository
  managerAccess: ManagerAccessView
  notify: (kind: 'success' | 'error', message: string) => void
  onOpenClinicSettings: (tenantUid: string) => void
  onClose: () => void
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const tenant = useTenant()
  const navigate = useNavigate()
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  const emailId = useId()
  const emailHintId = useId()
  const existingId = useId()
  const { phone, workspace } = useClinicStatus(clinic, api, readSide, true)
  const step = onboardingStep(clinic)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [grantTargetUid, setGrantTargetUid] = useState('')

  const managers = managerAccess.managers.filter((manager) =>
    manager.tenantUids.includes(clinic.tenantUid)
  )
  const invitations = managerAccess.invitations.filter(
    (invitation) => invitation.tenantUid === clinic.tenantUid
  )
  const candidates = managerAccess.managers.filter(
    (manager) => !manager.tenantUids.includes(clinic.tenantUid)
  )

  const invite = useMutation({
    mutationFn: (input: { email: string; resend: boolean }) =>
      api.createManagerInvitation!(user!, {
        email: input.email,
        tenantUid: clinic.tenantUid,
      }),
    onSuccess: async (_result, input) => {
      if (!input.resend) setEmail('')
      notify(
        'success',
        `${input.resend ? 'Invitation resent' : 'Invitation sent'} to ${input.email}.`
      )
      await managerAccess.refresh()
    },
  })
  const revokeInvitation = useMutation({
    mutationFn: (invitation: ManagerInvitation) =>
      api.revokeManagerInvitation!(user!, invitation.invitationId),
    onSuccess: async (_result, invitation) => {
      setConfirmation(null)
      notify('success', `Invitation for ${invitation.email} cancelled.`)
      await managerAccess.refresh()
    },
  })
  const revokeManager = useMutation({
    mutationFn: (manager: ManagerAccess) =>
      api.revokeClinicManager!(user!, {
        operationId: operationId('revoke'),
        targetUid: manager.targetUid,
        expectedMembershipRevision: manager.membershipRevision,
        tenantUid: clinic.tenantUid,
      }),
    onSuccess: async (_result, manager) => {
      setConfirmation(null)
      notify('success', `${managerLabel(manager)} removed from ${clinic.name}.`)
      await managerAccess.refresh()
    },
  })
  const grantManager = useMutation({
    mutationFn: (manager: ManagerAccess) =>
      api.grantClinicManager!(user!, {
        operationId: operationId('grant'),
        targetUid: manager.targetUid,
        expectedMembershipRevision: manager.membershipRevision,
        tenantUid: clinic.tenantUid,
      }),
    onSuccess: async (_result, manager) => {
      setGrantTargetUid('')
      notify(
        'success',
        `${managerLabel(manager)} can now manage ${clinic.name}.`
      )
      await managerAccess.refresh()
    },
  })
  const busy =
    invite.isPending ||
    revokeInvitation.isPending ||
    revokeManager.isPending ||
    grantManager.isPending
  usePatientDialog(dialog, {
    onClose: () => (confirmation ? setConfirmation(null) : onClose()),
    locked: busy,
  })
  // The question replaces the button that asked it: focus moves to the safe
  // answer, then back to that button (or the invite field once it is gone).
  const returnFocus = useRef<string | null>(null)
  const confirming = confirmation !== null && managerAccess.verified
  useEffect(() => {
    const node = dialog.current
    if (!node) return
    if (confirming) {
      node.querySelector<HTMLElement>('[data-autofocus]')?.focus()
      return
    }
    if (returnFocus.current === null) return
    const label = returnFocus.current
    returnFocus.current = null
    ;(
      Array.from(node.querySelectorAll<HTMLElement>('button')).find(
        (button) => button.getAttribute('aria-label') === label
      ) ?? node.querySelector<HTMLElement>('input, button')
    )?.focus()
  }, [confirming])
  function ask(next: Confirmation, label: string) {
    returnFocus.current = label
    setConfirmation(next)
  }

  const canGrant =
    managerAccess.verified && !!api.grantClinicManager && candidates.length > 0
  const grantTarget = candidates.find(
    (manager) => manager.targetUid === grantTargetUid
  )
  const inClinicList = tenant.authorizedClinics.some(
    (item) => item.id === clinic.tenantUid
  )

  function submitInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!managerAccess.verified) return
    const problem = emailProblem(email)
    setEmailError(problem)
    if (problem) return
    invite.mutate({ email: email.trim(), resend: false })
  }

  const confirmBusy = revokeManager.isPending || revokeInvitation.isPending
  const confirmError = revokeManager.isError
    ? managerAccessError(revokeManager.error)
    : revokeInvitation.isError
      ? cancelInvitationError(revokeInvitation.error)
      : null

  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.clinicDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className={panel.dialogHeader}>
          <div>
            <span className={panel.eyebrow}>Clinic</span>
            <h2 id={titleId}>{clinic.name}</h2>
            <p className={styles.dialogMeta}>
              Workspace <Status view={workspace} />
            </p>
          </div>
          <button
            type="button"
            className={panel.closeButton}
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {confirmation && confirming ? (
          <>
            <div className={panel.confirmBody}>
              {confirmation.kind === 'remove' ? (
                <>
                  <h2>Remove manager?</h2>
                  <p>
                    Remove {managerLabel(confirmation.manager)} as manager of{' '}
                    {clinic.name}?
                  </p>
                  <p>
                    This removes access to this clinic only. Their Flio account
                    will not be deleted.
                  </p>
                </>
              ) : (
                <>
                  <h2>
                    {confirmation.invitation.status === 'CLAIMING'
                      ? 'Cancel acceptance?'
                      : 'Cancel invitation?'}
                  </h2>
                  <p>
                    {confirmation.invitation.status === 'CLAIMING'
                      ? `Cancel the in-progress acceptance for ${confirmation.invitation.email} to manage ${clinic.name}? This is allowed only after the active claim window has ended and no manager access was granted.`
                      : `Cancel the invitation for ${confirmation.invitation.email} to manage ${clinic.name}?`}
                  </p>
                </>
              )}
              {confirmError ? (
                <p className={panel.fieldError} role="alert">
                  <AlertTriangle size={14} aria-hidden="true" />
                  {confirmError}
                </p>
              ) : null}
            </div>
            <div className={panel.dialogFooter}>
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  data-autofocus
                  disabled={confirmBusy}
                  onClick={() => {
                    revokeManager.reset()
                    revokeInvitation.reset()
                    setConfirmation(null)
                  }}
                >
                  {confirmation.kind === 'remove'
                    ? 'Keep manager'
                    : 'Keep invitation'}
                </Button>
                <Button
                  type="button"
                  className={panel.dangerSolid}
                  aria-disabled={confirmBusy || undefined}
                  onClick={() => {
                    if (confirmBusy) return
                    if (confirmation.kind === 'remove')
                      revokeManager.mutate(confirmation.manager)
                    else revokeInvitation.mutate(confirmation.invitation)
                  }}
                >
                  {confirmBusy
                    ? confirmation.kind === 'remove'
                      ? 'Removing…'
                      : 'Cancelling…'
                    : confirmation.kind === 'remove'
                      ? 'Remove manager'
                      : confirmation.invitation.status === 'CLAIMING'
                        ? 'Cancel acceptance'
                        : 'Cancel invitation'}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={panel.dialogBody}>
              {step ? (
                <div
                  className={`${panel.notice} ${styles.dialogNotice}`}
                  data-tone="warning"
                >
                  <AlertTriangle size={16} aria-hidden="true" />
                  <div>
                    <strong>Next prerequisite: {step}.</strong>
                    <p>
                      Continue through the clinic’s authorized onboarding owner.
                    </p>
                    {clinic.onboardingStatus === 'CRM_CREDENTIAL_REQUIRED' ? (
                      <Link
                        className={styles.inlineLink}
                        to={`/onboarding/without-pms?tenantUid=${encodeURIComponent(clinic.tenantUid)}`}
                      >
                        Choose PMS setup
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <section
                className={styles.group}
                aria-labelledby={`${titleId}-phone`}
              >
                <h3 id={`${titleId}-phone`} className={styles.groupTitle}>
                  <Phone size={14} aria-hidden="true" /> Phone &amp; texting
                </h3>
                <div className={styles.phoneCard}>
                  <div className={styles.phoneFacts}>
                    <Status view={phone} />
                    {phone.numberPending ? (
                      <Skeleton width="7rem" height="1rem" />
                    ) : (
                      <span className={styles.phoneNumber}>
                        {phoneNumberLabel(phone.number)}
                      </span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant={phone.connected ? 'secondary' : 'primary'}
                    onClick={() =>
                      void navigate(
                        `/organization/clinics/${encodeURIComponent(clinic.tenantUid)}/communications`
                      )
                    }
                  >
                    {phone.actionLabel}
                  </Button>
                </div>
                {phone.detail ? (
                  <p className={styles.groupNote} role="status">
                    {phone.detail}
                  </p>
                ) : null}
              </section>

              <section
                className={styles.group}
                aria-labelledby={`${titleId}-managers`}
              >
                <h3 id={`${titleId}-managers`} className={styles.groupTitle}>
                  <Users size={14} aria-hidden="true" /> Managers
                </h3>
                {managerAccess.verified ? (
                  <>
                    {managers.length === 0 && invitations.length === 0 ? (
                      <p className={styles.groupEmpty}>
                        No managers yet. Invite someone to manage this clinic.
                      </p>
                    ) : (
                      <ul className={styles.peopleList}>
                        {managers.map((manager) => (
                          <li key={manager.targetUid}>
                            <span className={styles.avatar} aria-hidden="true">
                              {initials(managerLabel(manager))}
                            </span>
                            <span className={styles.personText}>
                              <strong>{managerLabel(manager)}</strong>
                              <small>
                                {manager.tenantUids.length === 1
                                  ? 'This clinic only'
                                  : `${plural(manager.tenantUids.length, 'clinic', 'clinics')} in this organization`}
                              </small>
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              className={styles.removeButton}
                              aria-label={`Remove ${managerLabel(manager)}`}
                              disabled={busy}
                              onClick={() =>
                                ask(
                                  { kind: 'remove', manager },
                                  `Remove ${managerLabel(manager)}`
                                )
                              }
                            >
                              Remove
                            </Button>
                          </li>
                        ))}
                        {invitations.map((invitation) => (
                          <li key={invitation.invitationId} data-invitation>
                            <span className={styles.avatar} aria-hidden="true">
                              <Mail size={15} />
                            </span>
                            <span className={styles.personText}>
                              <strong>{invitation.email}</strong>
                              <small>
                                Invitation · {invitationStatus(invitation)}
                              </small>
                            </span>
                            <span className={styles.rowActions}>
                              {invitation.status === 'ACTIVE' &&
                              !invitation.expired ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  disabled={busy}
                                  aria-label={`Resend invitation to ${invitation.email}`}
                                  onClick={() =>
                                    invite.mutate({
                                      email: invitation.email,
                                      resend: true,
                                    })
                                  }
                                >
                                  Resend
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                variant="ghost"
                                className={styles.removeButton}
                                disabled={busy}
                                aria-label={`${invitation.status === 'CLAIMING' ? 'Cancel acceptance' : 'Cancel invitation'} for ${invitation.email}`}
                                onClick={() =>
                                  ask(
                                    { kind: 'cancel', invitation },
                                    `${invitation.status === 'CLAIMING' ? 'Cancel acceptance' : 'Cancel invitation'} for ${invitation.email}`
                                  )
                                }
                              >
                                Cancel
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {managers.length > 0 && invitations.length > 0 ? (
                      <p className={styles.groupNote}>
                        Manager access and invitations are separate. Removing a
                        manager does not cancel invitations, and cancelling an
                        invitation does not remove manager access.
                      </p>
                    ) : null}

                    <form
                      className={styles.inviteForm}
                      onSubmit={submitInvite}
                      noValidate
                    >
                      <label className={panel.field} htmlFor={emailId}>
                        <span>Invite a manager</span>
                      </label>
                      <div className={styles.inlineForm}>
                        <input
                          id={emailId}
                          className={styles.input}
                          type="email"
                          autoComplete="email"
                          placeholder="name@clinic.com"
                          value={email}
                          aria-invalid={emailError ? true : undefined}
                          aria-describedby={emailHintId}
                          readOnly={busy}
                          onChange={(event) => {
                            setEmail(event.target.value)
                            if (emailError) setEmailError(null)
                            if (invite.isError) invite.reset()
                          }}
                        />
                        <Button type="submit" disabled={busy}>
                          <Send size={15} aria-hidden="true" />
                          {invite.isPending && !invite.variables?.resend
                            ? 'Sending…'
                            : 'Send invitation'}
                        </Button>
                      </div>
                      {emailError ? (
                        <p
                          id={emailHintId}
                          className={panel.fieldError}
                          role="alert"
                        >
                          <AlertTriangle size={14} aria-hidden="true" />
                          {emailError}
                        </p>
                      ) : invite.isError ? (
                        <p
                          id={emailHintId}
                          className={panel.fieldError}
                          role="alert"
                        >
                          <AlertTriangle size={14} aria-hidden="true" />
                          {invitationError(invite.error)}
                        </p>
                      ) : (
                        <p id={emailHintId} className={panel.hint}>
                          Access is limited to {clinic.name}. They choose their
                          own password.
                        </p>
                      )}
                    </form>

                    {canGrant ? (
                      <form
                        className={styles.inviteForm}
                        onSubmit={(event) => {
                          event.preventDefault()
                          if (grantTarget) grantManager.mutate(grantTarget)
                        }}
                      >
                        <label className={panel.field} htmlFor={existingId}>
                          <span>Existing manager</span>
                        </label>
                        <div className={styles.inlineForm}>
                          <select
                            id={existingId}
                            className={styles.input}
                            value={grantTargetUid}
                            disabled={busy}
                            onChange={(event) => {
                              setGrantTargetUid(event.target.value)
                              if (grantManager.isError) grantManager.reset()
                            }}
                          >
                            <option value="">Choose a manager</option>
                            {candidates.map((manager) => (
                              <option
                                key={manager.targetUid}
                                value={manager.targetUid}
                              >
                                {managerLabel(manager)}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="submit"
                            variant="secondary"
                            disabled={busy || !grantTarget}
                          >
                            <UserPlus size={15} aria-hidden="true" />
                            {grantManager.isPending
                              ? 'Adding…'
                              : 'Add existing manager'}
                          </Button>
                        </div>
                        {grantManager.isError ? (
                          <p className={panel.fieldError} role="alert">
                            <AlertTriangle size={14} aria-hidden="true" />
                            {managerAccessError(grantManager.error)}
                          </p>
                        ) : (
                          <p className={panel.hint}>
                            Adds {clinic.name} to their access. No email is
                            sent.
                          </p>
                        )}
                      </form>
                    ) : null}
                  </>
                ) : managerAccess.pending ? (
                  <div className={styles.peopleSkeleton} role="status">
                    <span className={panel.srOnly}>Loading managers</span>
                    <Skeleton height="2.75rem" />
                    <Skeleton height="2.75rem" width="80%" />
                  </div>
                ) : (
                  <div className={styles.groupError} role="alert">
                    <p>Manager access couldn’t be loaded safely.</p>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={managerAccess.retry}
                    >
                      <RefreshCw size={14} aria-hidden="true" /> Retry
                    </Button>
                  </div>
                )}
              </section>
            </div>
            <footer className={`${panel.dialogFooter} ${styles.clinicFooter}`}>
              <Button
                type="button"
                variant="ghost"
                disabled={!inClinicList || busy}
                onClick={() => {
                  if (tenant.selectClinic(clinic.tenantUid))
                    onOpenClinicSettings(clinic.tenantUid)
                }}
              >
                Clinic settings <ArrowUpRight size={15} aria-hidden="true" />
              </Button>
              <div className={panel.footerActions}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={onClose}
                >
                  Done
                </Button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>,
    document.body
  )
}
